import { createAsyncThunk } from '@reduxjs/toolkit'
import { Patient } from '@dataTypes/PatientInfo.types'
import {
  GetAllPatients,
  AddNewPatient,
  UpdatePatient,
  DeletePatient,
  updatePatientPhotoById,
} from '@services/api/patient.api'
import { mockPatients } from '@assets/mockdata/PatientTray/mockPatients'
import { isPatientTrayMocksEnabled } from '@utils/patientTrayMocks.util'
import { logger } from '@utils/safeLogger'
import { RootState } from '@store/index'
import { fetchAllContacts } from './contact.action'
import { fetchAllConversations } from './chat.action'
import { fetchAllCallLogs } from './calls.action'
import { fetchAllInternalChats } from './chat-internal.action'

export const fetchAllPatients = createAsyncThunk<Patient[], void>(
  'patients/fetchAllPatients',
  async (_, { rejectWithValue }) => {
    try {
      const response = await GetAllPatients()

      if (response.statusCode !== 200) {
        if (isPatientTrayMocksEnabled()) {
          logger.warn(
            'API call failed, using mock patients data for development',
          )
          return mockPatients
        }
        return rejectWithValue(response.message) as any
      }

      if (response.data && response.data.patients) {
        return response.data.patients as unknown as Patient[]
      }

      if (isPatientTrayMocksEnabled()) {
        logger.warn(
          'Patients not found in response, using mock patients data for development',
        )
        return mockPatients
      }

      return rejectWithValue('Patients not found') as any
    } catch (error: any) {
      if (isPatientTrayMocksEnabled()) {
        logger.warn(
          'API call error, using mock patients data for development',
          { message: error.message },
        )
        return mockPatients
      }
      return rejectWithValue(error.message || 'An error occurred') as any
    }
  },
)

export const addPatient = createAsyncThunk(
  'patients/addPatient',
  async (
    patientData: Omit<Partial<Patient>, 'id'>,
    { getState, rejectWithValue },
  ) => {
    const state = getState() as RootState
    const clinicId = state.clinic.selectedClinic?.clinic_id
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await AddNewPatient({
        ...patientData,
        clinic_id: clinicId,
        user_id: user.user_id,
        created_by: user.user_id,
        updated_by: user.user_id,
      } as Omit<Partial<Patient>, 'id'> & {
        created_by: string
        updated_by: string
      })
      if (response.statusCode !== 201) {
        throw new Error(response.message || 'Failed to add patient')
      }

      if (response.data) {
        return response.data.patient
      }

      return 'Patients not found'
    } catch (error: any) {
      return error.message || 'An error occurred'
    }
  },
)

export const updatePatient = createAsyncThunk(
  'patients/updatePatient',
  async (patientData: Partial<Patient>, { dispatch, rejectWithValue }) => {
    try {
      if (!patientData.patient_id) {
        return rejectWithValue('Patient ID is required.')
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await UpdatePatient({
        ...patientData,
        updated_by: user.user_id,
      } as Partial<Patient> & { updated_by: string })
      if (response.statusCode !== 200) {
        throw new Error(response.message || 'Failed to update patient')
      }

      dispatch(fetchAllContacts())
      dispatch(fetchAllConversations())
      dispatch(fetchAllInternalChats())
      dispatch(fetchAllCallLogs())

      if (response.data) {
        return response.data.patient
      }

      return 'Patients not found'
    } catch (error: any) {
      return error.message || 'An error occurred'
    }
  },
)

export const removePatient = createAsyncThunk(
  'patients/removePatient',
  async (id: string, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Patient ID is required for deletion.')
      }

      const response = await DeletePatient(id)
      if (response.statusCode !== 200) {
        throw new Error(response.message || 'Failed to update patient')
      }

      return id
    } catch (error) {
      return rejectWithValue('Failed to delete patient.')
    }
  },
)

export const updatePatientPhoto = createAsyncThunk(
  'patients/updatePatientPhoto',
  async (
    { patientId, file }: { patientId: string; file: File },
    { dispatch, rejectWithValue },
  ) => {
    try {
      if (!patientId) {
        return rejectWithValue('Patient ID is required.')
      }
      if (!file) {
        return rejectWithValue('File is required.')
      }

      const response = await updatePatientPhotoById(patientId, file)
      if (response.statusCode !== 200) {
        throw new Error(response.message || 'Failed to update patient')
      }

      dispatch(fetchAllContacts())
      dispatch(fetchAllConversations())
      dispatch(fetchAllInternalChats())
      dispatch(fetchAllCallLogs())

      if (response.data) {
        return response.data.patient
      }

      return 'Patients not found'
    } catch (error: any) {
      return error.message || 'An error occurred'
    }
  },
)
